/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import { useRegistration } from '../context/RegistrationContext';
import { functions } from '../firebase';
import { setCookie } from '../utils/cookieUtils';
import { saveViewToken } from '../components/register/HealthDataSummary';

const mono = { fontFamily: "'DM Mono', monospace" };
const blue = '#0A66C2';
const gray = { text: '#94A6B8', border: '#D6E2EB', bg: '#FBFCFE', dark: '#131B20', mid: '#4C5967' };

function FitbitCallback() {
  const {
    participantId, setParticipant,
    loading, setLoading, handleError,
  } = useRegistration();

  const [searchParams] = useSearchParams();
  const { addAlert } = useAlert();
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Set when the participant's Google Account has no Google Health profile yet.
  const [notLinkedUrl, setNotLinkedUrl] = useState(null);

  const code  = searchParams.get('code');
  const state = searchParams.get('state');

  const handleCompleteFitbitAuth = async () => {
    if (loading) return;
    setLoading(true);
    if (!agreeTerms) { handleError(null, 'Please agree to the terms and conditions'); return; }
    try {
      const completeHealthAuthorization = httpsCallable(functions, 'completeHealthAuthorization');
      const res = await completeHealthAuthorization({ code, state, participantId });
      if (res?.data?.participant?.health?.connected) {
        setParticipant(res.data.participant);
        addAlert('success', 'Google Health authorization completed successfully');
        // The server identifies the participant from the authorization request; use its answer
        // rather than the cookie, which may be stale.
        const p = res.data.participant;
        const id = res.data.participantId || participantId;
        setCookie('participantId', id, 7);
        // Opens "Your data" on the completion step (this tab only, valid for 2 hours).
        saveViewToken(res.data.viewToken);
        let regUrl = '/reg/prolificRegistration/';
        if (p?.registrationType === 'direct')
          regUrl = '/reg/emailRegistrationDirect/' + p.projectId + '/code/' + id;
        else if (p?.registrationType === 'email')
          regUrl = '/reg/emailRegistration/' + p.projectId + '/code/' + id;
        else if (p?.projectId)
          regUrl = '/reg/prolificRegistration/' + p.projectId + '/pId/' + (p.registration?.prolific?.pId || id);
        if (!regUrl) { handleError(null, 'Failed to get registration URL'); }
        else { window.location.href = regUrl; }
      } else {
        handleError(null, 'Google Health authorization failed');
        setLoading(false);
      }
    } catch (err) {
      const key = err?.details?.key;
      if (key === 'account-not-linked') {
        setNotLinkedUrl(err.details.redirectUri || 'https://fitbit.google.com/auth/signup');
        setLoading(false);
        return;
      }
      if (key) {
        const messages = {
          'already-exists': 'This Google Health account is already connected to another participant in this study.',
          'missing-permissions': 'No health data permissions were granted. Please start again and allow access.',
          'expired': 'This link expired. Please go back and connect your device again.',
          'invalid-state': 'This link was already used or is invalid. Please go back and connect again.',
        };
        addAlert('error', messages[key] || err.message);
      } else {
        addAlert('error', 'An unknown error occurred');
      }
      handleError(err, 'Google Health authorization failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: gray.bg }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${gray.border}`, borderTopColor: blue, animation: 'ja-spin 0.8s linear infinite' }} />
        <style>{`@keyframes ja-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (notLinkedUrl) {
    return (
      <div style={{
        minHeight: '100vh', background: gray.bg, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: "'DM Sans', sans-serif", padding: '24px 16px',
      }}>
        <div style={{
          background: '#fff', border: `1px solid ${gray.border}`, borderRadius: 12,
          padding: '36px 40px', width: '100%', maxWidth: 480, boxShadow: '0 4px 24px rgba(9,89,170,0.07)',
        }}>
          <h2 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 16px', textAlign: 'center' }}>
            One more step
          </h2>
          <p style={{ fontSize: 14, color: gray.mid, lineHeight: 1.7, marginBottom: 20 }}>
            Your Google Account isn&apos;t linked to Google Health yet. Open the Google Health (Fitbit) app
            and sign in with your Google Account, or move your Fitbit account to Google. Then come back
            to this study link and connect again.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <a href={notLinkedUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: blue, fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              Set up Google Health →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: gray.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif", padding: '24px 16px',
    }}>
      <div style={{
        background: '#fff', border: `1px solid ${gray.border}`, borderRadius: 12,
        padding: '36px 40px', width: '100%', maxWidth: 480,
        boxShadow: '0 4px 24px rgba(9,89,170,0.07)',
      }}>
        {/* Wordmark */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <span style={{ ...mono, fontSize: 18, fontWeight: 500, color: blue, letterSpacing: '0.5px' }}>
            jamasp
          </span>
        </div>

        <h2 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 20px', textAlign: 'center' }}>
          Consent
        </h2>

        <p style={{ fontSize: 14, color: gray.mid, lineHeight: 1.7, marginBottom: 16 }}>
          Hereby, I give my consent to anonymously share my Fitbit wearable data (accessed through
          Google Health) for this study.
        </p>

        <ul style={{ paddingLeft: 20, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <li style={{ fontSize: 14, color: gray.mid, lineHeight: 1.65 }}>
            I understand that my data will be kept confidential and used for research purposes only (
            <Link to="/privacy" style={{ color: blue, textDecoration: 'none' }}>Privacy Policy</Link>).
          </li>
          <li style={{ fontSize: 14, color: gray.mid, lineHeight: 1.65 }}>
            I understand that I can withdraw my consent at any time (
            <Link to="/terms" style={{ color: blue, textDecoration: 'none' }}>Terms of Service</Link>).
          </li>
        </ul>

        {/* Checkbox */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 28 }}>
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={() => setAgreeTerms(!agreeTerms)}
            style={{ accentColor: blue, width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
          />
          <span style={{ fontSize: 14, color: gray.dark, lineHeight: 1.5 }}>
            I accept the Terms of Service and Privacy Policy.
          </span>
        </label>

        {/* Action */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            disabled={!agreeTerms || loading}
            onClick={handleCompleteFitbitAuth}
            style={{
              padding: '9px 26px', borderRadius: 8, border: 'none',
              cursor: !agreeTerms || loading ? 'not-allowed' : 'pointer',
              background: !agreeTerms || loading ? gray.border : `linear-gradient(to bottom, #0A66C2, #0959AA)`,
              color: !agreeTerms || loading ? gray.text : '#fff',
              fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500,
              boxShadow: !agreeTerms || loading ? 'none' : 'inset 0 1px rgba(255,255,255,0.15), 0 0 0 1px #064079',
              transition: 'all 80ms',
            }}
          >
            Agree &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default FitbitCallback;
